import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

import config from './config';

/**
 * New Relic Browser (RUM) agent for vitalstatistix.
 *
 * Runs in parallel with the existing Datadog RUM setup (see ./datadog.ts). The
 * license key is a public browser ingest key, not a secret. Config values live
 * in the deployed vitalstatistix.<env>.json files under `newrelic` (absent
 * locally and in federal/EU, which would need their own compliant endpoints).
 */
let agent: BrowserAgent | undefined;

export const initNewRelicRUM = () => {
  if (agent) {
    return;
  }

  const { licenseKey, applicationID, accountID, agentID, trustKey, beacon } = config.newrelic ?? {};

  if (!licenseKey || !applicationID) {
    return;
  }

  const nrBeacon = beacon || 'bam.nr-data.net';

  agent = new BrowserAgent({
    // `info` is strictly typed as strings.
    info: {
      applicationID,
      licenseKey,
      beacon: nrBeacon,
      errorBeacon: nrBeacon,
      sa: 1,
    },
    // `loader_config` is loosely typed and expects numeric ids.
    loader_config: {
      accountID: Number(accountID),
      agentID: Number(agentID),
      applicationID: Number(applicationID),
      licenseKey,
      trustKey: Number(trustKey),
    },
    init: {
      ajax: { deny_list: [nrBeacon] },
      distributed_tracing: { enabled: true },
      performance: { capture_detail: false, capture_marks: false, capture_measures: true },
      privacy: { cookies_enabled: true },
    },
  });

  // Mirror the Datadog `env` dimension (see ./datadog.ts) so staging/prod
  // traffic can be filtered apart within a single New Relic application. Uses
  // the OTel `deployment.environment.name` semantic-convention key for the
  // attribute.
  agent.setCustomAttribute('deployment.environment.name', config.launchdarkly.environment);
};
